import apiClient from './apiClient';

export enum AdminRoleType {
  SUPER_ADMIN = 'super_admin',
  MODERATOR = 'moderator',
  SUPPORT_ADMIN = 'support_admin',
}

export interface Permission {
  resource: string;
  actions: string[];
}

export interface AdminRole {
  _id: string;
  name: string;
  type: AdminRoleType;
  description?: string;
  permissions: Permission[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminActionLog {
  _id: string;
  admin: {
    _id: string;
    username: string;
    name: string;
    email?: string;
  };
  actionType: string;
  resource: string;
  resourceId?: string;
  description: string;
  details?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

class AdminRoleService {
  async getAllRoles(): Promise<AdminRole[]> {
    const response = await apiClient.get('/admin/roles');
    return response.data.data;
  }

  async getRoleById(id: string): Promise<AdminRole> {
    const response = await apiClient.get(`/admin/roles/${id}`);
    return response.data.data;
  }

  async createRole(roleData: Partial<AdminRole>): Promise<AdminRole> {
    const response = await apiClient.post('/admin/roles', roleData);
    return response.data.data;
  }

  async updateRole(id: string, roleData: Partial<AdminRole>): Promise<AdminRole> {
    const response = await apiClient.put(`/admin/roles/${id}`, roleData);
    return response.data.data;
  }

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/admin/roles/${id}`);
  }

  async assignRoleToUser(userId: string, roleId: string): Promise<void> {
    await apiClient.post('/admin/roles/assign', { userId, roleId });
  }

  async removeRoleFromUser(userId: string): Promise<void> {
    await apiClient.delete(`/admin/roles/user/${userId}`);
  }

  async getActionLogs(params?: {
    page?: number;
    limit?: number;
    adminId?: string;
    resource?: string;
    actionType?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ logs: AdminActionLog[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.adminId) queryParams.append('adminId', params.adminId);
    if (params?.resource) queryParams.append('resource', params.resource);
    if (params?.actionType) queryParams.append('actionType', params.actionType);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await apiClient.get(`/admin/roles/logs/actions?${queryParams.toString()}`);
    return response.data.data;
  }
}

export const adminRoleService = new AdminRoleService();
export default adminRoleService;

